import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Sparkles, Trophy, Heart } from "lucide-react";

export default function Leaderboard() {
  const [tab, setTab] = useState("compassionate");
  const [allTime, setAllTime] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [a, w] = await Promise.all([
          api.get("/leaderboard"),
          api.get("/leaderboard/compassionate"),
        ]);
        setAllTime(a.data.leaders);
        setWeekly(w.data.leaders);
      } catch (e) { setError(formatApiError(e)); }
    })();
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-14" data-testid="leaderboard-page">
      <div className="animate-fade-up">
        <span className="tag inline-flex items-center gap-2">
          <Trophy size={14} /> The Sabhā Honors
        </span>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-[#2D3B31] mt-5 leading-tight">
          Voices that lift the room.
        </h1>
        <p className="mt-5 text-[#5C6B5F] leading-relaxed max-w-2xl">
          Compassion, gratitude, and gentle reflection earn JSK Blessing Points.
          These are the seekers whose words have warmed our gathering most this week, and across all time.
        </p>
      </div>

      <div className="mt-10 flex gap-2 border-b" style={{ borderColor: "#EAE5D9" }}>
        <TabBtn active={tab === "compassionate"} onClick={() => setTab("compassionate")} testId="leaderboard-tab-compassionate">
          <Heart size={14} /> Most Compassionate (this week)
        </TabBtn>
        <TabBtn active={tab === "all"} onClick={() => setTab("all")} testId="leaderboard-tab-all">
          <Sparkles size={14} /> All-time JSK
        </TabBtn>
      </div>

      {error && <div className="mt-6 text-sm text-[#C05B3B]" data-testid="leaderboard-error">{error}</div>}

      {tab === "compassionate" && (
        <Section list={weekly} loading={weekly === null} empty="No compassionate voices yet this week — be the first." testid="leaderboard-weekly">
          {(row, idx) => (
            <Row idx={idx} name={row.name} primary={`${row.positive_count} kind ${row.positive_count === 1 ? "post" : "posts"}`} secondary={`+${row.points_earned} JSK earned this week`} />
          )}
        </Section>
      )}

      {tab === "all" && (
        <Section list={allTime} loading={allTime === null} empty="No seekers yet — start a thread to begin." testid="leaderboard-alltime">
          {(row, idx) => (
            <Row idx={idx} name={row.name} primary={`${row.points} JSK`} secondary="Total blessing points" />
          )}
        </Section>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px inline-flex items-center gap-1.5 ${
        active ? "border-[#D96C4A] text-[#D96C4A]" : "border-transparent text-[#5C6B5F] hover:text-[#2D3B31]"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function Section({ list, loading, empty, testid, children }) {
  if (loading) {
    return (
      <div className="mt-10 space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-16 rounded-2xl bg-[#F3EFE9] animate-pulse" />
        ))}
      </div>
    );
  }
  if (!list || list.length === 0) {
    return (
      <div className="mt-10 organic-card p-10 text-center" data-testid={`${testid}-empty`}>
        <p className="text-[#5C6B5F]">{empty}</p>
      </div>
    );
  }
  return (
    <ol className="mt-10 space-y-3" data-testid={testid}>
      {list.map((r, idx) => (
        <li key={r.id} className="organic-card p-5 animate-fade-up" style={{ animationDelay: `${Math.min(idx * 0.04, 0.4)}s` }} data-testid={`leader-${r.id}`}>
          {children(r, idx)}
        </li>
      ))}
    </ol>
  );
}

function Row({ idx, name, primary, secondary }) {
  const isPodium = idx < 3;
  const podiumColor = ["#D96C4A", "#A8845E", "#8C6F4D"][idx] || "#5C6B5F";
  return (
    <div className="flex items-center gap-4">
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center font-serif-display text-xl shrink-0"
        style={{
          background: isPodium ? podiumColor : "#F3EFE9",
          color: isPodium ? "#FFFFFF" : "#5C6B5F",
        }}
      >
        {idx + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-serif-display text-xl text-[#2D3B31] truncate">{name}</div>
        <div className="text-xs text-[#8A948C] mt-0.5">{secondary}</div>
      </div>
      <div className="font-serif-display text-2xl text-[#D96C4A] shrink-0">
        {primary}
      </div>
    </div>
  );
}
